import { useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { ModalContainer, ModalList, type ModalListItem } from 'src/components/ModalContainer';
import { useCurrentUserProfile } from 'src/graphql';
import { useAiPrompts } from 'src/graphql/entities/ai-prompt';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';

export const AiPromptModal = () => {
  const { user } = useCurrentUserProfile();
  const [search, setSearch] = useState('');
  const { isOpened, modalProps } = useModalState(Modals.AiPrompts);
  const { closeModal } = useModalMethods(Modals.AiPrompts);
  const { aiPrompts, loading: isLoadingPrompts } = useAiPrompts();

  const items = useMemo<ModalListItem[]>(
    () => aiPrompts.map((prompt) => ({ id: prompt.id, title: prompt.friendlyName })),
    [aiPrompts],
  );

  const submitPrompt = async (promptId: string, promptName: string, customPrompt = '') => {
    if (!modalProps || isNullish(user?.id)) return;
    closeModal();
    await modalProps.onSubmit?.(promptId, promptName, customPrompt);
  };

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="AI prompts"
      onClose={closeModal}
      data-testid="ai-prompts-modal"
    >
      <ModalList
        items={items}
        loading={isLoadingPrompts}
        placeholder="Type a prompt or select an option…"
        emptyText="No prompts match."
        onSearchChange={setSearch}
        onSelect={(item) => void submitPrompt(item.id, item.title)}
        createAction={
          !isLoadingPrompts && search
            ? {
                label: `Use custom prompt: ${search}`,
                onSelect: () => void submitPrompt('', search, search),
                showWhen: 'noMatches',
              }
            : undefined
        }
        testId="ai-prompts-modal"
      />
    </ModalContainer>
  );
};
